import _ from 'lodash';
import React, { ReactNode } from 'react';
import { NoData } from 'src/@vymo/ui/blocks';
import History from 'src/@vymo/ui/components/history';
import { InputFieldConfig } from 'src/@vymo/ui/molecules/form/types';
import { createSelector } from '@reduxjs/toolkit';
import { Keys, locale } from 'src/i18n';
import { getDetails } from 'src/models/details/selectors';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { RootState } from 'src/store';
import ChildComponent from 'src/workspace/childComponent';
// eslint-disable-next-line vymo-ui/restrict-import
import { isAdminOrSU } from 'src/workspace/queries';
import { getDomainConfigByType, getRevyreState } from '../selectors';
import { DomainType, WorkflowType } from '../types';
import { allowedTypes, CASE, CURRENT_STATE_ID_CODE } from './constants';
import { transformQuickAction } from './queries';
import { ProfileCardFormSelectorArgs } from './types';
import styles from './index.module.scss';

export const getReferredAllowedActions = (state: RootState) =>
  (getRevyreState(state)?.referredEntityAllowedActions || []).map((action) => ({
    ...action,
    code: action?.allowedAction,
    name: action?.eventName,
  }));

export const getProfileDetails = createSelector(
  [getDetails],
  (entityDetails) => {
    const { profiles = [] } = entityDetails ?? {};
    return profiles;
  },
);

export const getAllowedActions = createSelector(
  [
    (state: RootState, id: string) =>
      (getDetails(state, id)?.allowedActions || []).map((action) => ({
        ...action,
        code: action?.allowedAction,
        name: action?.eventName,
      })),
    (state: RootState, entityId, domainType: DomainType) => domainType,
  ],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (allowedActions, domainType) => allowedActions,
  // TODO - if in add activity disposition should be filled with filters
  // domainType === 'activity'
  //   ? allowedActions.filter(({ linkedActivity }) => linkedActivity)
  //   : allowedActions,
);

export const getNextEventAllowedAction = createSelector(
  [
    (state: RootState, id: string) =>
      (getDetails(state, id)?.allowedActions || []).map((action) => ({
        ...action,
        code: action?.allowedAction,
        name: action?.eventName,
      })),
    (state: RootState, entityId, domainType: DomainType) => domainType,
  ],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (allowedActions, domainType) =>
    domainType === 'activity'
      ? allowedActions.filter(({ linkedActivity }) => linkedActivity)
      : allowedActions,
);

export const getQuickActions = createSelector([getDetails], (entityDetails) => {
  const { quickActions = [], profiles = [] } = entityDetails ?? {};
  return quickActions
    .filter((action) => allowedTypes.includes(action?.type))
    .map((action) => transformQuickAction(action, profiles));
});

export const getHeaderActionButtons = createSelector(
  [
    (state, entityId, domainType) =>
      getAllowedActions(state, entityId, domainType),
    (state, entityId, domainType, workflowType) => workflowType,
  ],
  (allowedActions, workflowType) => {
    const buttons: {
      hint: string;
      type: string;
      disabled: boolean;
    }[] = [];

    if (workflowType !== CASE) {
      buttons.push({
        hint: 'Update',
        type: 'update',
        disabled: !allowedActions?.length,
      });
    }

    return buttons;
  },
);

export const getEntityActivitiesBasedOnCode = createSelector(
  [getDetails, (state: RootState, entityId: string, code: string) => code],
  (entityDetails, code) => entityDetails.activities?.[code] ?? [],
);

export const getProfileCardInputs = createSelector(
  [
    getDomainConfigByType,
    (
      state: RootState,
      domainType: DomainType,
      workflowType: WorkflowType,
      entityId: string,
    ) => getDetails(state, entityId),
  ],
  (domainConfig, entityDetails) => {
    const inputs = domainConfig?.detailsConfig?.inputs ?? {};
    const { profileCardData, inputsMap } = entityDetails ?? {};

    const headerInputCodes = (profileCardData?.header || []).map(
      (input) => input?.profileField,
    );
    const bodyInputCodes = (profileCardData?.body || []).map(
      (input) => input?.profileField,
    );

    const headerInputs = headerInputCodes
      ?.map((code) => {
        const input = _.find(inputs, { code });

        if (input) {
          return {
            ...input,
            value: _.get(inputsMap, code),
          };
        }
        return {
          code,
          type: 'text',
          hint: code === 'currentState.stateId' ? 'Current State' : code,
          value: _.get(inputsMap, code),
        };
      })
      .filter((inp) => inp?.value && inp.hint);
    const bodyInputs = bodyInputCodes
      ?.map((code) => {
        const input = _.find(inputs, { code });

        if (input) {
          return {
            ...input,
            value: _.get(inputsMap, code),
          };
        }
        return {
          code,
          type: 'text',
          hint: code === 'currentState.stateId' ? 'Current State' : code,
          value: _.get(inputsMap, code),
        };
      })
      .filter((inp) => inp?.value && inp.hint);

    return {
      headerInputs,
      bodyInputs,
    };
  },
);

export const getEntityDetailsInputsMap = createSelector(
  [
    (state, entityId) => getDetails(state, entityId),
    (_state, _entityId, params?) => params,
  ],
  (entityDetails, params = {}) => {
    const { activityType, workflowType, type, isBlockingScreen } = params;

    const inputsMap = entityDetails?.inputsMap ?? {};

    return {
      ...inputsMap,
      ...(workflowType &&
        activityType &&
        ['create', 'detailsCreate'].includes(type) && {
          workflowType,
          activityType,
        }),
      ...(isBlockingScreen && {
        selectState: 'complete',
        medium: 'CALL',
      }),
    };
  },
);

export const getReferredEntityDetails = createSelector(
  [(state, entityId) => getDetails(state, entityId)],
  (entityDetails) => entityDetails?.referredEntityDetails ?? [],
);

export const selectParentActivityDetailInputs =
  (parentAssociatedWorkflow: string, activityType: string) =>
  (state: RootState) =>
    state?.revyre?.modules?.[parentAssociatedWorkflow]?.activityConfig?.find(
      (activityConfig) => activityConfig.type === activityType,
    )?.detail_inputs;

export const getEntityDetailsInputsAndGroups = createSelector(
  [
    (state) => getRevyreConfig(state),
    (state, domainType, workflowType, entityId) => getDetails(state, entityId),
    (state, domainType, workflowType, entityId) =>
      getEntityDetailsInputsMap(state, entityId),
    (state, domainType) => domainType,
    (state, domainType, workflowType) => workflowType,
  ],
  (
    revyreConfig,
    entityDetails,
    entityDetailsInputMap,
    domainType,
    workflowType,
  ) => {
    const domainConfig = revyreConfig?.[domainType];
    const workflowConfig = revyreConfig?.[workflowType];
    const { createConfig } = domainConfig ?? {};
    let { detailsConfig } = domainConfig ?? workflowConfig ?? {};

    const { activityType, workflowType: parentAssociatedWorkflow } =
      entityDetailsInputMap;

    if (parentAssociatedWorkflow && activityType) {
      detailsConfig = {
        inputs: revyreConfig?.[parentAssociatedWorkflow]?.activityConfig?.find(
          (config) => config.type === activityType,
        )?.detail_inputs,
        groups: [],
      };
    }

    const { inputs, groups = [] } = detailsConfig ??
      createConfig ?? { inputs: [], groups: [] };

    const { inputsMap } = entityDetails ?? {};

    const filteredInputs = _.filter(
      inputs,
      (input) => inputsMap?.[input?.code],
      // && input?.type !== 'referral',
    ) as InputFieldConfig[];

    return { inputs: filteredInputs, groups };
  },
);

export const getProfileCardFormValues = createSelector(
  [
    (_state: RootState, params: ProfileCardFormSelectorArgs) => params.inputs,
    (_state: RootState, params: ProfileCardFormSelectorArgs) =>
      params.domainType,
    (_state: RootState, params: ProfileCardFormSelectorArgs) =>
      params.workflowType,
    getRevyreConfig,
  ],
  (inputs, domainType, workflowType, revyreConfig) => {
    const valuesMap: Record<string, any> = {};

    if (!_.isEmpty(inputs)) {
      inputs.forEach((input) => {
        let value = input?.value;

        if (input.code === CURRENT_STATE_ID_CODE) {
          const typeKey = workflowType || domainType;
          const states = revyreConfig?.[typeKey]?.states || [];
          const correspondingState = states.find(
            (event) => event?.id === value,
          );
          value = correspondingState?.name;
        }
        valuesMap[input?.code] = {
          value,
        };
      });
    }

    return valuesMap;
  },
);

export const getActivityTabs = createSelector(
  [
    (_state: RootState, params) => params.domainType,
    (_state: RootState, params) => params.workflowType,
    (_state: RootState, params) =>
      getEntityActivitiesBasedOnCode(_state, params.entityId, 'audit'),
    (_state: RootState) => getRevyreConfig(_state),
  ],
  (domainType, workflowType, auditActivities, revyreConfig) => {
    const tabs: {
      key: string;
      label: string;
      children: ReactNode;
      order?: number;
    }[] = [];

    const events = revyreConfig?.[workflowType || domainType]?.events || [];

    if (domainType === 'workflow') {
      tabs.push({
        key: 'activities',
        label: locale(Keys.TRAILS),
        order: 1,
        children: (
          <div className={styles.details__entityList}>
            <ChildComponent
              id="REVYRE_LIST"
              props={{
                key: 'activities',
                mode: 'details',
                domainType: 'activity',
              }}
            />
          </div>
        ),
      });
    }

    if (isAdminOrSU()) {
      tabs.push({
        key: 'audit',
        label: locale(Keys.AUDIT),
        children:
          auditActivities?.length === 0 ? (
            <NoData message={locale(Keys.NO_ACTIVITIES_FOUND)} />
          ) : (
            auditActivities.map((activity, index) => {
              const correspondingEvent = events.find(
                (event) => event?.allowedAction === activity?.eventId,
              );

              const data = {
                history: [
                  { ...activity, config: correspondingEvent?.config?.inputs },
                ],
                date: activity?.eventTime,
                action_type:
                  correspondingEvent?.actionName ||
                  _.capitalize(activity?.toState),
              };

              return (
                <History
                  data={data}
                  open={index === 0}
                  viewType="form"
                  classNames={styles.auditHistoryTab}
                />
              );
            })
          ),
      });
    }

    return tabs;
  },
);

export const getProfileDetailsInputsGroupsAndValues = createSelector(
  [(state: RootState, entityId: string) => getDetails(state, entityId)],
  (entityDetails) => {
    const { profiles = [] } = entityDetails ?? {};
    const inputs: InputFieldConfig[] = [];
    const values: Record<string, { value: string }> = {};
    const fields: string[] = [];

    profiles.forEach((profile, index) => {
      const code = `profile_${index + 1}`;
      const hint = profile?.description || `Profile ${index + 1}`;
      const value = profile?.name || '';

      inputs.push({
        code,
        default_value: null,
        hidden: false,
        hint,
        oifOptions: null,
        placeholder: '',
        read_only: false,
        required: true,
        source: undefined,
        type: 'text',
      });

      values[code] = { value };
      fields.push(code);
    });

    const groups = fields.length
      ? [
          {
            code: 'profileGroup',
            name: locale(Keys.BORROWERS),
            fields,
          },
        ]
      : [];

    return { inputs, values, groups };
  },
);
